import { useState, useCallback } from "react";
import {
  MonthlyChecklistEntity,
  ChecklistItemEntity,
  ProgressStatsEntity,
} from "../../domain/entities/MonthlyChecklistEntity";
import { MonthlyChecklistRepositoryImpl } from "../../data/repositories/MonthlyChecklistRepositoryImpl";
import {
  GetChecklistByIdUseCase,
  GetChecklistItemsUseCase,
  AddChecklistItemUseCase,
  UpdateChecklistItemUseCase,
  DeleteChecklistItemUseCase,
} from "../../domain/use-cases/monthly-checklist";

// Create repository instance (singleton)
const repository = new MonthlyChecklistRepositoryImpl();

// Create use cases
const getChecklistByIdUseCase = new GetChecklistByIdUseCase(repository);
const getChecklistItemsUseCase = new GetChecklistItemsUseCase(repository);
const addChecklistItemUseCase = new AddChecklistItemUseCase(repository);
const updateChecklistItemUseCase = new UpdateChecklistItemUseCase(repository);
const deleteChecklistItemUseCase = new DeleteChecklistItemUseCase(repository);

/** Aligns with ChecklistDetailPage overall progress (completed-style tasks). */
function recomputeProgressFromItemsByArea(
  itemsByArea: Record<string, ChecklistItemEntity[]>
): ProgressStatsEntity {
  const all = Object.values(itemsByArea).flat();
  const total = all.length;
  const completed = all.filter((i) =>
    ["completed", "done", "approved", "to_be_done_later"].includes(i.status)
  ).length;
  const in_progress = all.filter((i) => i.status === "in_progress").length;
  const pending_review = all.filter(
    (i) =>
      i.status === "pending_review" || i.status === "submitted_for_review"
  ).length;
  const not_started = all.filter((i) => i.status === "not_started").length;
  return { total, completed, in_progress, not_started, pending_review };
}

function mergeItemUpdateIntoAreas(
  prev: Record<string, ChecklistItemEntity[]>,
  itemId: number,
  updates: Partial<
    Omit<
      ChecklistItemEntity,
      "id" | "checklist_id" | "created_at" | "updated_at" | "template_item_id"
    >
  >
): Record<string, ChecklistItemEntity[]> {
  let sourceArea: string | null = null;
  let idx = -1;
  for (const [area, list] of Object.entries(prev)) {
    const i = list.findIndex((x) => x.id === itemId);
    if (i >= 0) {
      sourceArea = area;
      idx = i;
      break;
    }
  }
  if (sourceArea === null) {
    return prev;
  }

  const oldItem = prev[sourceArea][idx];
  const merged: ChecklistItemEntity = {
    ...oldItem,
    ...updates,
    updated_at: new Date().toISOString(),
  };

  const targetArea = merged.accounting_area;
  const next: Record<string, ChecklistItemEntity[]> = { ...prev };

  if (sourceArea !== targetArea) {
    next[sourceArea] = prev[sourceArea].filter((x) => x.id !== itemId);
    if (next[sourceArea].length === 0) {
      delete next[sourceArea];
    }
    const targetList = [...(next[targetArea] || []), merged].sort(
      (a, b) => a.order_index - b.order_index
    );
    next[targetArea] = targetList;
    return next;
  }

  next[sourceArea] = prev[sourceArea]
    .map((x) => (x.id === itemId ? merged : x))
    .sort((a, b) => a.order_index - b.order_index);
  return next;
}

export interface UseMonthlyChecklistReturn {
  checklist: MonthlyChecklistEntity | null;
  itemsByArea: Record<string, ChecklistItemEntity[]>;
  loading: boolean;
  loadingMore: Record<string, boolean>;
  hasMore: Record<string, boolean>;
  error: Error | null;
  fetchChecklist: (checklistId: number) => Promise<void>;
  fetchItems: (checklistId: number) => Promise<void>;
  loadMoreItems: (checklistId: number, accountingArea: string) => Promise<void>;
  addItem: (
    checklistId: number,
    item: Omit<ChecklistItemEntity, "id" | "checklist_id" | "created_at" | "updated_at">
  ) => Promise<ChecklistItemEntity>;
  updateItem: (
    checklistId: number,
    itemId: number,
    updates: Partial<
      Omit<
        ChecklistItemEntity,
        "id" | "checklist_id" | "created_at" | "updated_at" | "template_item_id"
      >
    >
  ) => Promise<void>;
  deleteItem: (itemId: number, accountingArea: string) => Promise<void>;
}

export const useMonthlyChecklist = (): UseMonthlyChecklistReturn => {
  const [checklist, setChecklist] = useState<MonthlyChecklistEntity | null>(null);
  const [itemsByArea, setItemsByArea] = useState<Record<string, ChecklistItemEntity[]>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<Record<string, boolean>>({});
  const [hasMore, setHasMore] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<Error | null>(null);

  const fetchChecklist = useCallback(async (checklistId: number) => {
    try {
      setLoading(true);
      setError(null);
      const checklistData = await getChecklistByIdUseCase.execute(checklistId);
      setChecklist(checklistData);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to fetch checklist");
      setError(error);
      console.error("Failed to fetch checklist:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchItems = useCallback(
    async (checklistId: number) => {
      try {
        setLoading(true);
        setError(null);
        const groupedItems = await getChecklistItemsUseCase.execute(checklistId);
        
        // Replace items with server data (no merging to avoid duplicates)
        // This is the initial load or manual refresh, so we want fresh data
        setItemsByArea(groupedItems);
        
        // Initialize hasMore for each area (assuming 5 items per area initially)
        const hasMoreState: Record<string, boolean> = {};
        Object.keys(groupedItems).forEach((area) => {
          // If we got 5 items, there might be more
          hasMoreState[area] = groupedItems[area].length >= 5;
        });
        setHasMore(hasMoreState);
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error("Failed to fetch items");
        setError(error);
        console.error("Failed to fetch items:", error);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const loadMoreItems = useCallback(
    async (checklistId: number, accountingArea: string) => {
      if (loadingMore[accountingArea] || !hasMore[accountingArea]) {
        return;
      }

      try {
        setLoadingMore((prev) => ({ ...prev, [accountingArea]: true }));
        setError(null);
        
        const currentItems = itemsByArea[accountingArea] || [];
        const offset = currentItems.length;
        const limit = 5;

        const result = await repository.getChecklistItemsByArea(
          checklistId,
          accountingArea,
          offset,
          limit
        );

        setItemsByArea((prev) => ({
          ...prev,
          [accountingArea]: [...(prev[accountingArea] || []), ...result.items],
        }));

        setHasMore((prev) => ({
          ...prev,
          [accountingArea]: result.hasMore,
        }));
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error("Failed to load more items");
        setError(error);
        console.error("Failed to load more items:", error);
      } finally {
        setLoadingMore((prev) => ({ ...prev, [accountingArea]: false }));
      }
    },
    [itemsByArea, loadingMore, hasMore]
  );

  const addItem = useCallback(
    async (
      checklistId: number,
      item: Omit<ChecklistItemEntity, "id" | "checklist_id" | "created_at" | "updated_at">
    ): Promise<ChecklistItemEntity> => {
      try {
        setError(null);
        const newItem = await addChecklistItemUseCase.execute(checklistId, item);
        
        // Add the item locally to state immediately for instant feedback
        setItemsByArea((prev) => {
          const area = item.accounting_area;
          const areaItems = prev[area] || [];

          if (areaItems.find((i) => i.id === newItem.id)) {
            return prev;
          }

          const sortedItems = [...areaItems, newItem].sort(
            (a, b) => a.order_index - b.order_index
          );

          const next = {
            ...prev,
            [area]: sortedItems,
          };

          setChecklist((c) =>
            c
              ? {
                  ...c,
                  progress: recomputeProgressFromItemsByArea(next),
                }
              : c
          );

          return next;
        });

        setHasMore((prev) => ({
          ...prev,
          [item.accounting_area]: true,
        }));

        return newItem;
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error("Failed to add item");
        setError(error);
        throw error;
      }
    },
    []
  );

  const updateItem = useCallback(
    async (
      checklistId: number,
      itemId: number,
      updates: Partial<
        Omit<
          ChecklistItemEntity,
          "id" | "checklist_id" | "created_at" | "updated_at" | "template_item_id"
        >
      >
    ): Promise<void> => {
      try {
        setError(null);
        await updateChecklistItemUseCase.execute(itemId, updates);

        setItemsByArea((prev) => {
          const next = mergeItemUpdateIntoAreas(prev, itemId, updates);
          setChecklist((c) =>
            c && c.id === checklistId
              ? {
                  ...c,
                  progress: recomputeProgressFromItemsByArea(next),
                }
              : c
          );
          return next;
        });
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error("Failed to update item");
        setError(error);
        throw error;
      }
    },
    []
  );

  const deleteItem = useCallback(
    async (itemId: number, accountingArea: string): Promise<void> => {
      try {
        setError(null);
        await deleteChecklistItemUseCase.execute(itemId);
        
        setItemsByArea((prev) => {
          const areaItems = prev[accountingArea] || [];
          const filteredItems = areaItems.filter((item) => item.id !== itemId);
          const next: Record<string, ChecklistItemEntity[]> = {
            ...prev,
            [accountingArea]: filteredItems,
          };
          if (filteredItems.length === 0) {
            delete next[accountingArea];
          }
          setChecklist((c) =>
            c
              ? {
                  ...c,
                  progress: recomputeProgressFromItemsByArea(next),
                }
              : c
          );
          return next;
        });
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error("Failed to delete item");
        setError(error);
        throw error;
      }
    },
    []
  );

  return {
    checklist,
    itemsByArea,
    loading,
    loadingMore,
    hasMore,
    error,
    fetchChecklist,
    fetchItems,
    loadMoreItems,
    addItem,
    updateItem,
    deleteItem,
  };
};

